import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { Tag, Plus, Edit2, Trash2, X, Check, ShoppingCart, Car, Utensils, Film, Heart, ShoppingBag, Zap, HelpCircle, Briefcase, Laptop, Gift, RotateCcw, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

const iconMap = {
  'shopping-cart': ShoppingCart,
  'car': Car,
  'utensils': Utensils,
  'film': Film,
  'heart': Heart,
  'shopping-bag': ShoppingBag,
  'zap': Zap,
  'help-circle': HelpCircle,
  'briefcase': Briefcase,
  'laptop': Laptop,
  'gift': Gift,
  'rotate-ccw': RotateCcw,
  'plus-circle': PlusCircle,
  'tag': Tag,
};

const availableColors = [
  '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', 
  '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#84CC16'
];

const Categories = () => {
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('expense');
  
  // New category form
  const [showForm, setShowForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', type: 'expense', color: '#6B7280' });
  const [saving, setSaving] = useState(false);
  
  // Edit mode
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const [expenseRes, incomeRes] = await Promise.all([
        api.get('/categories/expense'),
        api.get('/categories/income'),
      ]);
      setExpenseCategories(expenseRes.data);
      setIncomeCategories(incomeRes.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      toast.error('Please enter category name');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/categories', {
        name: newCategory.name.trim(),
        type: newCategory.type,
        color: newCategory.color,
      });
      toast.success('Category created!');
      setNewCategory({ name: '', type: activeTab, color: '#6B7280' });
      setShowForm(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (categoryId) => {
    if (!editName.trim()) {
      toast.error('Please enter category name');
      return;
    }
    
    try {
      await api.put(`/categories/${categoryId}`, {
        name: editName.trim(),
        color: editColor,
      });
      toast.success('Category updated!');
      setEditingId(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? All transactions with this category will be moved to "Uncategorized".`)) {
      return;
    }
    
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setEditName(category.name);
    setEditColor(category.color || '#6B7280');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditColor('');
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Tag;
    return IconComponent;
  };

  const currentCategories = activeTab === 'expense' ? expenseCategories : incomeCategories;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
              Categories
            </h1>
            <p className="text-lg text-muted-foreground">
              Manage your income and expense categories
            </p>
          </div>
          <button
            onClick={() => {
              setNewCategory({ ...newCategory, type: activeTab });
              setShowForm(true);
            }}
            data-testid="add-category-button"
            className="bg-white text-black hover:bg-white/90 px-4 py-2 rounded-full font-medium transition-all flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 p-1 bg-white/5 rounded-xl">
          <button
            onClick={() => setActiveTab('expense')}
            data-testid="tab-expense"
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'expense' ? 'bg-white text-black' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Expenses ({expenseCategories.length})
          </button>
          <button
            onClick={() => setActiveTab('income')}
            data-testid="tab-income"
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'income' ? 'bg-white text-black' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Income ({incomeCategories.length})
          </button>
        </div>

        {/* New Category Form */}
        {showForm && (
          <div className="mb-6 p-6 bg-white/5 border border-white/10 rounded-2xl">
            <h3 className="text-lg font-semibold mb-4">New Category</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Coffee Shop"
                  data-testid="input-category-name"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Type
                </label>
                <select
                  value={newCategory.type}
                  onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value })}
                  data-testid="select-category-type"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="expense" className="bg-[#0f172a]">Expense</option>
                  <option value="income" className="bg-[#0f172a]">Income</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewCategory({ ...newCategory, color })}
                      className={`w-8 h-8 rounded-full transition-all ${
                        newCategory.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-[#030712]' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-category-button"
                  className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-full font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2 rounded-full font-medium border border-white/10 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div className="space-y-3">
          {currentCategories.map((category) => {
            const IconComponent = getIcon(category.icon);
            const isEditing = editingId === category.id;
            
            return (
              <div
                key={category.id}
                data-testid={`category-${category.id}`}
                className="p-4 bg-white/5 border border-white/10 rounded-xl flex items-center justify-between group"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${category.color || '#6B7280'}20` }}
                  >
                    <IconComponent 
                      className="w-5 h-5" 
                      style={{ color: category.color || '#6B7280' }} 
                    />
                  </div>
                  
                  {isEditing ? (
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="px-3 py-1 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        autoFocus
                      />
                      <div className="flex space-x-1">
                        {availableColors.slice(0, 5).map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditColor(color)}
                            className={`w-5 h-5 rounded-full ${
                              editColor === color ? 'ring-2 ring-white' : ''
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">{category.name}</p>
                      {category.is_system && (
                        <span className="text-xs text-muted-foreground">System</span>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => handleUpdate(category.id)}
                        className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      {!category.is_system && (
                        <>
                          <button
                            onClick={() => startEdit(category)}
                            className="p-2 text-muted-foreground hover:text-white hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id, category.name)}
                            className="p-2 text-muted-foreground hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {currentCategories.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No categories</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories;
